import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { WorkHandoff } from '@shared/types/thread-work';
import { ACCOUNT_TASK_COMPLETED_EVENT } from '@/components/activity/AccountCapabilityLauncher';
import { type AppMember, loadAppMembers } from './app-records';
import { supabase } from './supabase';
import { toDashboardUiError } from './dashboard-errors';

/** Relais (21/10/2026): hand one task to a colleague with a word. */
export const passTopic = async (params: { topicId: string; expectedTopicVersion: number; toUserId: string; note: string | null }) => {
  const { error } = await supabase.functions.invoke('activity-feed', { body: { action: 'pass_topic', ...params, note: params.note?.trim() || null } });
  if (error) throw new Error((await toDashboardUiError(error, 'La tâche n’a pas pu être passée.')).message);
  window.dispatchEvent(new CustomEvent(ACCOUNT_TASK_COMPLETED_EVENT));
};

/** The recipient ends a relay (the task is done) or sends it back with a reply. */
export const closeHandoff = async (params: { handoffId: string; outcome: 'done' | 'returned'; reply: string | null }) => {
  const { error } = await supabase.functions.invoke('activity-feed', { body: { action: 'close_handoff', ...params, reply: params.reply?.trim() || null } });
  if (error) throw new Error((await toDashboardUiError(error, 'Le relais n’a pas pu être mis à jour.')).message);
  window.dispatchEvent(new CustomEvent(ACCOUNT_TASK_COMPLETED_EVENT));
};

type HandoffRow = {
  id: string; topic_id: string; owner_user_id: string | null; from_user_id: string; to_user_id: string; note: string | null;
  title: string; context_label: string | null; context_href: string | null; status: WorkHandoff['status'];
  reply: string | null; created_at: string; closed_at: string | null;
};

const toHandoff = (row: HandoffRow): WorkHandoff => ({
  id: row.id, topicId: row.topic_id, ownerUserId: row.owner_user_id, fromUserId: row.from_user_id, toUserId: row.to_user_id,
  note: row.note, title: row.title, contextLabel: row.context_label, contextHref: row.context_href,
  status: row.status, reply: row.reply, createdAt: row.created_at, closedAt: row.closed_at,
});

/** Open and returned relays I can read (sent, received, on my work), most recent first. */
export const loadHandoffs = async (): Promise<WorkHandoff[]> => {
  const { data, error } = await supabase.from('work_handoffs').select('*').in('status', ['open', 'returned']).order('created_at', { ascending: false });
  if (error) throw new Error('Les relais ne sont pas accessibles pour le moment.');
  return ((data ?? []) as HandoffRow[]).map(toHandoff);
};

/** For an object's actions: which are passed to someone, and to whom. */
export const loadOpenHandoffTargets = async (topicIds: string[]): Promise<Record<string, string>> => {
  if (topicIds.length === 0) return {};
  const { data, error } = await supabase.from('work_handoffs').select('topic_id,to_user_id').eq('status', 'open').in('topic_id', topicIds);
  if (error) throw new Error('Les relais ne sont pas accessibles pour le moment.');
  return Object.fromEntries(((data ?? []) as Array<{ topic_id: string; to_user_id: string }>).map((row) => [row.topic_id, row.to_user_id]));
};

/** The company's people and me, read once per page. */
let teamPromise: Promise<{ me: string | null; members: AppMember[] }> | null = null;
export const loadTeam = () => {
  teamPromise ??= Promise.all([supabase.auth.getUser(), loadAppMembers()])
    .then(([{ data }, members]) => ({ me: data.user?.id ?? null, members }))
    .catch((cause) => { teamPromise = null; throw cause; });
  return teamPromise;
};

export const useTeam = () => {
  const [team, setTeam] = useState<{ me: string | null; members: AppMember[] } | null>(null);
  useEffect(() => { void loadTeam().then(setTeam).catch(() => setTeam({ me: null, members: [] })); }, []);
  return team;
};

export interface HandoffsView {
  /** Relays passed to me, still open: first rows of my file. */
  incoming: WorkHandoff[];
  /** Tasks passed to someone else: they leave the holder's file while open. */
  passedTopicIds: Set<string>;
  /** The reply of a relay sent back, on the task it came back to. */
  replies: Map<string, string>;
  nameOf: (userId: string) => string;
}

export const buildHandoffsView = (handoffs: WorkHandoff[], me: string | null, members: AppMember[]): HandoffsView => {
  const nameOf = (userId: string) => members.find((member) => member.userId === userId)?.name ?? 'Un collègue';
  const replies = new Map<string, string>();
  handoffs.forEach((handoff) => {
    if (handoff.status !== 'returned' || replies.has(handoff.topicId)) return;
    replies.set(handoff.topicId, handoff.reply ? `Réponse de ${nameOf(handoff.toUserId)} : ${handoff.reply}` : `Renvoyée par ${nameOf(handoff.toUserId)}`);
  });
  return {
    incoming: handoffs.filter((handoff) => handoff.status === 'open' && handoff.toUserId === me),
    passedTopicIds: new Set(handoffs.filter((handoff) => handoff.status === 'open' && handoff.toUserId !== me).map((handoff) => handoff.topicId)),
    replies,
    nameOf,
  };
};

/** My relays, kept live: a task passed to me appears without reloading the page. */
export const useWorkHandoffs = () => {
  const [handoffs, setHandoffs] = useState<WorkHandoff[]>([]);
  const team = useTeam();
  const channelId = useId();
  const reload = useCallback(() => {
    void loadHandoffs().then(setHandoffs).catch((cause) => console.error('work_handoffs_load_failed', cause));
  }, []);
  useEffect(() => {
    reload();
    // My own relays (passed, ended, sent back) refresh at once; others' arrive through Realtime.
    window.addEventListener(ACCOUNT_TASK_COMPLETED_EVENT, reload);
    const channel = supabase.channel(`work-handoffs-${channelId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'work_handoffs' }, reload)
      .subscribe();
    return () => {
      window.removeEventListener(ACCOUNT_TASK_COMPLETED_EVENT, reload);
      void supabase.removeChannel(channel);
    };
  }, [reload, channelId]);
  const view = useMemo(() => buildHandoffsView(handoffs, team?.me ?? null, team?.members ?? []), [handoffs, team]);
  return { ...view, reload, team };
};
